import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useProviderAuth } from '../../context/ProviderAuthContext';
import { TrendingUp, Users, Activity, LogOut, Building2, Mail, ChevronRight, Award, Zap, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import MeridianLogo from '../../components/MeridianLogo';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { StripeCheckoutModal } from '../../components/StripeCheckoutModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="bg-white border border-stone-200 p-6 card-hover" data-testid={testId}>
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm text-stone-500 font-medium">{label}</p>
        <div className="w-9 h-9 bg-[#F0EEE9] flex items-center justify-center">
          <Icon size={16} className="text-[#0D3D4A]" />
        </div>
      </div>
      <p className="text-3xl font-bold text-stone-900 mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>{value}</p>
      {sub && <p className="text-xs text-[#A8B4B8]">{sub}</p>}
    </div>
  );
}

export default function ProviderDashboardPage() {
  const { providerUser, providerToken, providerLogout, providerAuthHeaders } = useProviderAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [billingMessage, setBillingMessage] = useState('');
  const [billingMessageType, setBillingMessageType] = useState('');
  // Inline Stripe checkout modal state
  const [checkoutConfig, setCheckoutConfig] = useState(null); // { type, label, amount }

  useEffect(() => {
    if (!providerToken) { navigate('/provider-portal/login'); return; }
    fetchData();
  }, [providerToken]);

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API}/provider/billing/summary`, { headers: providerAuthHeaders });
      setBilling(res.data);
    } catch (e) {
      console.error('Failed to fetch billing summary', e);
    }
  };
  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/provider/stats`, { headers: providerAuthHeaders }),
        axios.get(`${API}/provider/leads`, { headers: providerAuthHeaders }),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/provider-portal/login');
    } finally {
      setLoading(false);
    }
    fetchBilling();
  };

  const handlePurchaseFeatured = () => {
    setBillingMessage('');
    setCheckoutConfig({
      type: 'featured',
      label: 'Verified Partner — Featured Placement (30 Days)',
      amount: 500,
    });
  };

  const handlePayLeadInvoice = () => {
    if (!billing || billing.lead_count_billable === 0) return;
    setBillingMessage('');
    setCheckoutConfig({
      type: 'leads',
      label: `Lead Referral Fees — ${billing.lead_count_billable} lead${billing.lead_count_billable !== 1 ? 's' : ''}`,
      amount: billing.outstanding_amount,
    });
  };

  const handleCheckoutSuccess = (type) => {
    setCheckoutConfig(null);
    setBillingMessage(
      type === 'featured'
        ? 'Featured placement activated! Your listing is now boosted for 30 days.'
        : 'Payment successful! Your lead balance has been cleared.'
    );
    setBillingMessageType('success');
    fetchBilling();
  };

  const handleCheckoutCancel = () => {
    setCheckoutConfig(null);
  };

  const handleLogout = () => {
    providerLogout();
    navigate('/provider-portal/login');
  };

  // Build chart data from stats
  const chartData = stats ? {
    labels: stats.leads_per_week?.map(w => w.week_label) || [],
    datasets: [{
      label: 'Patient Leads',
      data: stats.leads_per_week?.map(w => w.count) || [],
      backgroundColor: '#0D3D4A',
      hoverBackgroundColor: '#047857',
      borderRadius: 2,
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} lead${ctx.parsed.y !== 1 ? 's' : ''}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { family: 'Manrope, sans-serif', size: 11 }, color: '#78716c' }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { family: 'Manrope, sans-serif', size: 11 }, color: '#78716c' },
        grid: { color: '#f5f5f4' }
      }
    }
  };

  if (!providerToken) return null;

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Stripe inline checkout modal */}
      {checkoutConfig && (
        <StripeCheckoutModal
          type={checkoutConfig.type}
          label={checkoutConfig.label}
          amount={checkoutConfig.amount}
          providerToken={providerToken}
          onSuccess={handleCheckoutSuccess}
          onCancel={handleCheckoutCancel}
        />
      )}
      {/* Portal Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/"><MeridianLogo variant="light" /></Link>
              <span className="text-[#A8B4B8]">|</span>
              <span className="text-sm font-medium text-stone-600">Provider Portal</span>
            </div>
            <div className="flex items-center gap-4">
              {providerUser && (
                <span className="text-xs text-[#A8B4B8] hidden sm:block">{providerUser.email}</span>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-red-600 transition-colors"
                data-testid="provider-logout-btn"
              >
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-10 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center">
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-1">Provider Dashboard</p>
              <h1 className="text-2xl font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="provider-facility-name">
                {providerUser?.provider_name || providerUser?.email || 'Your Facility'}
              </h1>
            </div>
          </div>
          <div className="text-sm text-[#A8B4B8]">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 space-y-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[0,1,2].map(i => (
              <div key={i} className="bg-white border border-stone-200 p-6 animate-pulse">
                <div className="h-4 bg-stone-100 rounded w-1/2 mb-6" />
                <div className="h-8 bg-stone-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <StatCard
                label="Leads This Month"
                value={stats?.leads_this_month ?? 0}
                sub="Patient inquiries in current calendar month"
                icon={TrendingUp}
                testId="stat-leads-month"
              />
              <StatCard
                label="Total Leads (All Time)"
                value={stats?.total_leads ?? 0}
                sub="Cumulative patient referrals received"
                icon={Users}
                testId="stat-leads-total"
              />
              <StatCard
                label="Top Procedure"
                value={stats?.top_procedure || '—'}
                sub="Most searched procedure at your facility"
                icon={Activity}
                testId="stat-top-procedure"
              />
            </div>

            {/* Chart */}
            <div className="bg-white border border-stone-200 p-8" data-testid="leads-chart">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>Lead Volume — Last 8 Weeks</h2>
                  <p className="text-sm text-[#A8B4B8] mt-0.5">Weekly patient inquiry trends for your facility</p>
                </div>
              </div>
              <div className="h-52">
                {chartData && <Bar data={chartData} options={chartOptions} />}
              </div>
            </div>

            {/* Leads Table */}
            <div className="bg-white border border-stone-200" data-testid="leads-table">
              <div className="flex items-center justify-between px-8 py-5 border-b border-stone-100">
                <h2 className="text-lg font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>Patient Inquiries</h2>
                <span className="text-sm text-[#A8B4B8]">{leads.length} total</span>
              </div>

              {leads.length === 0 ? (
                <div className="py-16 text-center">
                  <Users size={36} className="text-stone-200 mx-auto mb-3" />
                  <p className="text-[#A8B4B8] font-medium text-sm">No leads yet</p>
                  <p className="text-[#A8B4B8] text-xs mt-1">When patients inquire about your facility, their anonymized details will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left px-8 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                        <th className="text-left px-8 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Procedure</th>
                        <th className="text-left px-8 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Patient Location</th>
                        <th className="text-left px-8 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {leads.map((lead, i) => (
                        <tr key={lead.id || i} className="hover:bg-[#F0EEE9] transition-colors" data-testid={`lead-row-${i}`}>
                          <td className="px-8 py-4 text-stone-600">
                            {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td className="px-8 py-4 font-medium text-stone-900">
                            {lead.procedure_searched || 'General Inquiry'}
                          </td>
                          <td className="px-8 py-4 text-stone-600">
                            {lead.patient_location || 'Unknown'}
                          </td>
                          <td className="px-8 py-4">
                            <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#F0EEE9] text-[#0D3D4A] px-2 py-1">
                              {lead.status || 'Initiated'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Featured Placement Billing Card */}
            {billingMessage && (
              <div className={`flex items-center gap-3 p-4 border ${billingMessageType === 'success' ? 'bg-[#F0EEE9] border-[#0D3D4A]/20 text-[#0D3D4A]' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="billing-message">
                {billingMessageType === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                <p className="text-sm font-medium">{billingMessage}</p>
              </div>
            )}

            {billing && !billing.featured && (
              <div className="bg-white border border-stone-200 p-8" data-testid="featured-upsell-card">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="w-12 h-12 bg-[#F0EEE9] border border-[#0D3D4A]/20 flex items-center justify-center flex-shrink-0">
                    <Award size={22} className="text-[#0D3D4A]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>Become a Verified Partner</h3>
                      <span className="text-xs bg-[#0D3D4A] text-white font-semibold px-2 py-0.5">$500 / month</span>
                    </div>
                    <p className="text-sm text-stone-500 leading-relaxed max-w-xl">
                      Get top placement in Meridian's provider directory, an exclusive <strong className="text-stone-700">Verified Partner</strong> badge on every listing, and priority visibility to patients searching in your state. Placement is active for 30 days per payment.
                    </p>
                    <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-stone-500">
                      <li className="flex items-center gap-1"><CheckCircle size={11} className="text-[#0D3D4A]" /> Top of search results</li>
                      <li className="flex items-center gap-1"><CheckCircle size={11} className="text-[#0D3D4A]" /> Verified Partner badge</li>
                      <li className="flex items-center gap-1"><CheckCircle size={11} className="text-[#0D3D4A]" /> 30-day placement</li>
                    </ul>
                  </div>
                  <button
                    onClick={handlePurchaseFeatured}
                    disabled={!!checkoutConfig}
                    className="bg-[#0D3D4A] text-white px-7 py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60 flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                    data-testid="purchase-featured-btn"
                  >
                    <CreditCard size={15} />
                    Upgrade Now
                  </button>
                </div>
                {billing.lead_count_billable > 0 && (
                  <div className="mt-6 pt-6 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-stone-900">Outstanding Lead Balance</p>
                      <p className="text-xs text-stone-500 mt-0.5">
                        {billing.lead_count_billable} qualified lead{billing.lead_count_billable !== 1 ? 's' : ''} &times; $300 = <strong className="text-stone-700">${billing.outstanding_amount.toLocaleString()}</strong>
                      </p>
                    </div>
                    <button
                      onClick={handlePayLeadInvoice}
                      disabled={!!checkoutConfig}
                      className="border border-[#A8B4B8] text-stone-700 px-5 py-2.5 text-sm font-medium hover:border-[#A8B4B8] hover:bg-[#F0EEE9] transition-colors disabled:opacity-60 flex items-center gap-2"
                      data-testid="pay-lead-invoice-btn"
                    >
                      <Zap size={13} /> Pay Lead Invoice
                    </button>
                  </div>
                )}
              </div>
            )}

            {billing && billing.featured && (
              <div className="bg-[#0D3D4A] border border-[#0D3D4A] p-8" data-testid="featured-status-card">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center flex-shrink-0">
                    <Award size={22} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white" style={{fontFamily: 'Outfit, sans-serif'}}>Verified Partner — Active</h3>
                      <span className="text-xs bg-white text-[#0D3D4A] font-bold px-2 py-0.5">FEATURED</span>
                    </div>
                    <p className="text-[#F0EEE9]/70 text-sm">
                      Your listing is featured at the top of Meridian's directory with a Verified Partner badge.
                      {billing.featured_expires_at && (
                        <span> Active until <strong className="text-white">{new Date(billing.featured_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={handlePurchaseFeatured}
                    disabled={!!checkoutConfig}
                    className="bg-white text-[#0D3D4A] px-6 py-3 text-sm font-semibold hover:bg-[#F0EEE9] transition-colors disabled:opacity-60 whitespace-nowrap"
                    data-testid="renew-featured-btn"
                  >
                    Renew (+30 days)
                  </button>
                </div>
              </div>
            )}

            {/* Partnership CTA */}
            <div className="bg-[#0D3D4A] p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="partnership-cta">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#0D3D4A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail size={18} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Ready to make Meridian an official referral partner?</h3>
                  <p className="text-[#F0EEE9]/70 text-sm leading-relaxed max-w-xl">
                    Contact us to discuss lead pricing, featured placement in search results, and exclusive referral partnership agreements. Official partners receive priority listing and verified badge placement.
                  </p>
                </div>
              </div>
              <a
                href="mailto:partners@meridiantracker.com"
                className="flex items-center gap-2 bg-white text-[#0D3D4A] px-6 py-3 text-sm font-semibold hover:bg-[#F0EEE9] transition-colors whitespace-nowrap flex-shrink-0"
                data-testid="contact-partnership-btn"
              >
                Contact partners@meridiantracker.com <ChevronRight size={15} />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
